import { type RefObject, useEffect, useState } from 'react';

/** The first and last day with a card in view. */
export interface VisibleDays {
    first: string;
    last: string;
}

/** What the strip shows: whether there is more either side, and which days are in view. */
export interface RailView {
    atStart: boolean;
    atEnd: boolean;
    visibleDays: VisibleDays | null;
}

const AT_REST: RailView = { atStart: true, atEnd: true, visibleDays: null };

// Chrome reports fractional scroll positions and rounds scrollWidth, so an end can measure
// a pixel short of where it is.
const TOLERANCE_PX = 1;

/** The `[data-day]` markers in the strip start each day's run of cards. */
export function measureRailView(track: HTMLElement): RailView {
    const range = track.scrollWidth - track.clientWidth;
    const left = track.scrollLeft;
    const right = left + track.clientWidth;

    const markers = Array.from(track.querySelectorAll<HTMLElement>('[data-day]'));
    const inView: string[] = [];
    markers.forEach((marker, index) => {
        const start = marker.offsetLeft;
        const end = markers[index + 1]?.offsetLeft ?? track.scrollWidth;
        const day = marker.dataset.day;
        if (day && start < right && end > left) {
            inView.push(day);
        }
    });

    return {
        atStart: range <= TOLERANCE_PX || left <= TOLERANCE_PX,
        atEnd: range <= TOLERANCE_PX || left >= range - TOLERANCE_PX,
        visibleDays:
            inView.length > 0 ? { first: inView[0], last: inView[inView.length - 1] } : null,
    };
}

function sameView(a: RailView, b: RailView): boolean {
    return (
        a.atStart === b.atStart &&
        a.atEnd === b.atEnd &&
        a.visibleDays?.first === b.visibleDays?.first &&
        a.visibleDays?.last === b.visibleDays?.last
    );
}

/**
 * Kept current as the strip scrolls, resizes, or gets a new set of cards. Measured once per
 * frame at most: a bioscope day can hold thousands of cards, so the measurement reads the
 * layout of the day markers only, never of the cards.
 */
export function useRailView(trackRef: RefObject<HTMLElement | null>): RailView {
    const [view, setView] = useState(AT_REST);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        let frame = 0;
        const update = () => {
            frame = 0;
            const next = measureRailView(track);
            setView((current) => (sameView(current, next) ? current : next));
        };
        const schedule = () => {
            if (frame === 0) {
                frame = requestAnimationFrame(update);
            }
        };
        update();
        track.addEventListener('scroll', schedule, { passive: true });
        // jsdom has no ResizeObserver; the measurement above still runs there.
        const observer =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        observer?.observe(track);
        // A new day, filter or image set changes the cards without resizing the strip.
        const cards = new MutationObserver(schedule);
        cards.observe(track, { childList: true });
        return () => {
            cancelAnimationFrame(frame);
            track.removeEventListener('scroll', schedule);
            observer?.disconnect();
            cards.disconnect();
        };
    }, [trackRef]);

    return view;
}
